import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, GitMerge, Plus } from "lucide-react";
import { discoveryApi, apiErrorMessage, isDedupReview } from "@/api/client";
import { toast } from "sonner";

/** Handles the full add-to-leads flow incl. dedup review (never surprise-merges). */
export function AddToLeadsDialog({ open, onOpenChange, prospect, onDone }) {
  const [opportunity, setOpportunity] = useState(false);
  const [candidates, setCandidates] = useState(null); // null=initial, [] = review mode
  const [overrideReason, setOverrideReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) { setOpportunity(false); setCandidates(null); setOverrideReason(""); setError(""); setResult(null); }
  }, [open]);

  const fsaId = prospect?.source_establishment_id;

  const submitInitial = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await discoveryApi.addToLeads(fsaId, { create_caterctrl_opportunity: opportunity });
      setResult(data);
      toast.success(data.tier === "exact" ? "Linked to existing lead" : "Added to leads");
      onDone?.();
    } catch (err) {
      if (isDedupReview(err)) {
        setCandidates(err.response.data.candidates || []);
      } else {
        setError(apiErrorMessage(err));
      }
    } finally { setBusy(false); }
  };

  const createNewAnyway = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await discoveryApi.addToLeads(fsaId, { override_dedup: true, create_caterctrl_opportunity: opportunity, override_reason: overrideReason || "created new despite possible duplicates" });
      setResult(data); toast.success("Created new lead"); onDone?.();
    } catch (err) { setError(apiErrorMessage(err)); } finally { setBusy(false); }
  };

  const mergeInto = async (c) => {
    setBusy(true); setError("");
    try {
      const { data } = await discoveryApi.addToLeads(fsaId, { override_dedup: true, force_match_organisation_id: c.organisation_id, force_match_location_id: c.location_id, override_reason: overrideReason || "confirmed same site" });
      setResult(data); toast.success("Merged into existing lead"); onDone?.();
    } catch (err) { setError(apiErrorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="add-to-leads-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add “{prospect?.business_name}” to Leads</DialogTitle></DialogHeader>

        {result ? (
          <div className="space-y-3 text-center" data-testid="add-result">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
            <p className="text-sm font-semibold text-slate-800">
              {result.tier === "exact" ? "Linked to an existing lead." : "Lead created."}
            </p>
            <button onClick={() => onOpenChange(false)} className="min-h-[44px] w-full rounded-lg bg-teal-700 font-semibold text-white hover:bg-teal-800">Done</button>
          </div>
        ) : candidates ? (
          <div className="space-y-3" data-testid="dedup-review">
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              Possible duplicates found. Choose to merge into an existing lead, or create a new one. Nothing is merged automatically.
            </p>
            {candidates.map((c, i) => (
              <div key={i} data-testid={`candidate-${i}`} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Match confidence</span>
                  <span className="text-sm font-bold text-teal-700">{Math.round(c.confidence * 100)}%</span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100"><div className="h-full rounded-full bg-teal-500" style={{ width: `${Math.round(c.confidence * 100)}%` }} /></div>
                <button data-testid={`merge-${i}`} onClick={() => mergeInto(c)} disabled={busy}
                  className="mt-2 flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50 text-sm font-semibold text-teal-700 hover:bg-teal-100">
                  <GitMerge className="h-4 w-4" /> Merge into this lead
                </button>
              </div>
            ))}
            <button data-testid="create-new-anyway" onClick={createNewAnyway} disabled={busy}
              className="flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg bg-slate-800 text-sm font-semibold text-white hover:bg-slate-900">
              <Plus className="h-4 w-4" /> Create new anyway
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" data-testid="add-opportunity-toggle" checked={opportunity} onChange={(e) => setOpportunity(e.target.checked)} className="h-4 w-4" />
              Also create a CaterCTRL opportunity (New)
            </label>
            {prospect?.is_suppressed && <p className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">This organisation is suppressed — adding it is still allowed but recorded.</p>}
            {error && <p data-testid="add-error" className="text-sm font-medium text-rose-600">{error}</p>}
            <button data-testid="add-submit" onClick={submitInitial} disabled={busy}
              className="min-h-[48px] w-full rounded-lg bg-teal-700 font-semibold text-white hover:bg-teal-800 disabled:opacity-60">
              {busy ? "Adding…" : "Add to Leads"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
